import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';

import { DeviceStatusType, Feature, FeatureNames, IsStringValueProperty } from '@vi-iot/client';

import { RoutingService } from '@services';
import {
  DevicesStore,
  InstallationFeaturesStore,
  RemoteServiceStore,
  RichDevice,
  isEcoWaterDevice,
} from '@store';

import { NO_VALUE } from '@pipes';
import { take, takeUntil } from 'rxjs/operators';
import { PillType } from '../../../shared/components/pill/pill.model';

@Component({
  selector: 'app-devices-list',
  templateUrl: './devices-list.component.html',
  styleUrls: ['./devices-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesListComponent implements OnInit, OnDestroy {
  readonly pillType = PillType;
  readonly NO_VALUE = NO_VALUE;
  isEcoWaterDevice = isEcoWaterDevice;
  DeviceStatusType = DeviceStatusType;
  urlDeviceSerial: string;
  urlGatewaySerial: string;

  devicesList: RichDevice[];

  @Input() devicesList$: Observable<RichDevice[]>;
  @Input() disabled: boolean;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private routingService: RoutingService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private remoteServiceStore: RemoteServiceStore,
    private devicesStore: DevicesStore
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.urlDeviceSerial = params?.deviceSerial;

      if (params?.gatewaySerial) {
        this.urlGatewaySerial = params?.gatewaySerial;
      }
      this.cdr.markForCheck();
    });

    this.devicesList$.pipe(take(1), takeUntil(this.ngUnsubscribe)).subscribe((devices) => {
      this.devicesList = devices;
    });

    this.initialDeviceSelection();
  }

  trackBy(_index: number, device: RichDevice): string {
    return device.boilerSerial;
  }

  selectDevice(device: RichDevice): void {
    this.updateUrl(device?.boilerSerial, device?.gatewaySerial);

    let matchingDevice = this.devicesList.find((dev) =>
      dev.boilerSerial
        ? dev.boilerSerial === device.boilerSerial
        : dev.gatewaySerial === device.gatewaySerial
    );

    this.setDevicesBasedOnType(matchingDevice);
  }

  getDeviceVariantFeature(device: RichDevice): Observable<Feature<IsStringValueProperty, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceVariant,
      device.id,
      device.gatewaySerial
    );
  }

  private updateUrl(deviceSerial: string, gatewaySerial: string): void {
    this.routingService.updateUrl({ deviceSerial, gatewaySerial });
  }

  private initialDeviceSelection(): void {
    const initialDevice = this.getDeviceBasedOnInitialSerial();

    this.setDevicesBasedOnType(initialDevice);
  }

  private getDeviceBasedOnInitialSerial(): RichDevice {
    let serialMatching: string;
    let matchingDevice: RichDevice;

    if (this.urlDeviceSerial) {
      serialMatching = this.urlDeviceSerial;
      matchingDevice = this.devicesList.find((dev) => dev.boilerSerial === serialMatching);
    } else {
      serialMatching = this.urlGatewaySerial;
      matchingDevice = this.devicesList.find((dev) => dev.gatewaySerial === serialMatching);
    }

    return matchingDevice;
  }

  private setDevicesBasedOnType(matchingDevice: RichDevice): void {
    this.devicesStore.setSelectedDevice(matchingDevice?.selectId);
    this.remoteServiceStore.changeSelectedDevice(matchingDevice);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
